/**
 * Minimal Copilot icon animation while a response is being generated.
 */
import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { AIAssistantIcon } from './AIAssistantIcon';
import { Colors } from '@styles/index';

const iconPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.88;
  }
`;

const ringPulse = keyframes`
  0% {
    transform: scale(0.85);
    opacity: 0.45;
  }
  70%, 100% {
    transform: scale(1.45);
    opacity: 0;
  }
`;

const labelPulse = keyframes`
  0%, 100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
`;

export const CopilotThinkingIndicator: React.FC = () => (
  <Box
    role="status"
    aria-live="polite"
    aria-label="AI Assistant is thinking"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      px: 1.25,
      py: 0.75,
      borderRadius: '10px',
      backgroundColor: Colors.white,
      border: '1px solid #E2E8F0',
      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        flexShrink: 0,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: '1.5px solid rgba(0, 120, 212, 0.3)',
          animation: `${ringPulse} 1.8s ease-out infinite`,
        }}
      />
      <Box
        aria-hidden
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: `${iconPulse} 1.8s ease-in-out infinite`,
        }}
      >
        <AIAssistantIcon size={18} />
      </Box>
    </Box>
    <Typography
      component="span"
      sx={{
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: '#64748B',
        whiteSpace: 'nowrap',
        animation: `${labelPulse} 2s ease-in-out infinite`,
      }}
    >
      Thinking...
    </Typography>
  </Box>
);

export default CopilotThinkingIndicator;
